import { ChangeDetectionStrategy, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiProfileSizes } from '../../../../shared/profile-icon/profile-icon.component';
import { FiDialogsContentBase } from '../../../../shared/fi-dialogs/fi-dialogs-content-base';
import { IKUserMatch, IPKUserMatch } from '../../pk';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AppTextService } from '../../../../shared/app-text';
import { BehaviorSubject } from 'rxjs';
import { isDefined } from '../../../../shared/utils';
import { UserModel } from '../../../../user/user.model';
import { FiContactsService } from '@fi-shared/fi-contacts/fi-contacts.service';
import { PKUsersImportType } from '@fi-app/external-platform-integration/pk/pk-users-import-type';
import { PkHelperService } from '@fi-app/external-platform-integration/pk/pk-helper.service';

interface IPKMatchDialogData {
    pkUserMatch: IKUserMatch;
    importedUsers: IPKUserMatch[];
    usersImportType: PKUsersImportType;
}

@Component({
    selector: 'fi-pk-user-match-dialog',
    templateUrl: './fi-pk-user-match-dialog.component.html',
    styleUrls: ['./fi-pk-user-match-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-pk-user-match-dialog' },
    standalone: false,
})
export class FiPkUserMatchDialogComponent
    extends FiDialogsContentBase<IPKMatchDialogData, IPKUserMatch>
    implements OnInit
{
    profilePictureSize = FiProfileSizes;
    pkUserFilterString: string = '';
    appTextService: AppTextService;
    availableUsers$: BehaviorSubject<IPKUserMatch[]> = new BehaviorSubject<IPKUserMatch[]>([]);
    chainformationUser: UserModel;
    private currentPkUser: IPKUserMatch;
    private fiContactsService: FiContactsService;
    private pkHelperService: PkHelperService;

    ngOnInit(): void {
        this.chainformationUser = this.data.pkUserMatch.chainformationUser;
        this.currentPkUser = this.data.pkUserMatch.pkUser;
        this.appTextService = this.injector.get(AppTextService);
        this.fiContactsService = this.injector.get(FiContactsService);
        this.pkHelperService = this.injector.get(PkHelperService);
        this.availableUsers$.next(this.data.importedUsers);
        this.pkUserFilterString = isDefined(this.currentPkUser) ? this.getMatchedUserLabel(this.currentPkUser) : '';
    }

    public isUserDisabled(user: IPKUserMatch): boolean {
        return isDefined(this.data.pkUserMatch.pkUser) && this.data.pkUserMatch.pkUser.Id === user.Id
            ? false
            : !!user.UserId;
    }

    public getChainformationUserRolesText(): string {
        return `${this.appTextService.get('common.roles')}: ${this.chainformationUser.Roles.map(
            (role) => role.Name,
        ).join(', ')}`;
    }

    public getChainformationUserIdText(): string {
        return `${this.appTextService.get('common.chainformation_user')}: ${this.chainformationUser.Id}`;
    }

    isChainformationUserHasEmail(): boolean {
        return (
            !!this.chainformationUser.EmailAddressContacts.length &&
            !!this.chainformationUser.EmailAddressContacts[0].ContactValue.length &&
            this.isEmailImport()
        );
    }
    isChainformationUserHasPhoneNumber(): boolean {
        return (
            !!this.chainformationUser.PhoneNumberContacts.length &&
            !!this.chainformationUser.PhoneNumberContacts[0].ContactValue.length &&
            this.isPhoneNumberImport()
        );
    }

    public getChainformationUserEmail(): string {
        return `${this.appTextService.get('common.email')}: ${this.chainformationUser.EmailAddressContacts[0].ContactValue}`;
    }

    public getChainformationUserPhoneNumber(): string {
        return `${this.appTextService.get('common.phone_number')}: ${this.fiContactsService.getFullContactValue(this.chainformationUser.PhoneNumberContacts[0])}`;
    }

    public getChainformationUserUnitNameText(): string {
        return `${this.appTextService.get('common.units')}: ${this.chainformationUser.PreferredBusinessUnitName}`;
    }

    public onUserSelect($event: MatAutocompleteSelectedEvent): void {
        this.currentPkUser = Object.assign({}, $event.option.value);
        this.pkUserFilterString = this.getMatchedUserLabel(this.currentPkUser);
    }

    public getSuggestionUserNameContact(user: IPKUserMatch): string {
        if (typeof user === 'string') return user;

        if (!user || !user.FirstName) return '';

        return `${user.FirstName} ${user.LastName} (${this.getUserContactValue(user)})`;
    }

    private getUserContactValue(user: IPKUserMatch): string {
        return this.pkHelperService.getUserContactValue(user, this.data.usersImportType);
    }

    isPhoneNumberImport(): boolean {
        return this.data.usersImportType === PKUsersImportType.ByPhoneNumber;
    }

    isEmailImport(): boolean {
        return this.data.usersImportType === PKUsersImportType.ByEmail;
    }

    public getSuggestionUserRolesUnit(user: IPKUserMatch): string {
        return `<b>${this.pkHelperService.getUserRoleNamesText(user)}
            </b> ${this.appTextService.get('common.at')} <b>${this.pkHelperService.getUserUnitNamesText(user)}</b>`;
    }

    public filterAvailableUsers(value: string): void {
        this.currentPkUser = undefined;
        if (typeof value === 'string') {
            this.availableUsers$.next(
                this.data.importedUsers.filter((user: IPKUserMatch) => {
                    return this.getMatchedUserLabel(user).toLowerCase().includes(value.toLowerCase());
                }),
            );
        }
    }

    getResult(): IPKUserMatch {
        return this.currentPkUser;
    }

    private getMatchedUserLabel(user: IPKUserMatch): string {
        return `${user.FirstName} ${user.LastName} (${this.getUserContactValue(user)}), ${this.pkHelperService.getUserUnitNamesText(user)}`;
    }
}
